import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus,
  Flame, MessageSquare, Heart, Clock, Target, Sparkles,
  X, Loader2, Check, Trash2, ExternalLink, AlertCircle, Download
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const MOOD_COLORS = {
  positive: "bg-emerald-400", neutral: "bg-amber-400", negative: "bg-red-400",
};
const MOOD_TEXT = {
  positive: "text-emerald-600 dark:text-emerald-400",
  neutral: "text-amber-600 dark:text-amber-400",
  negative: "text-red-600 dark:text-red-400",
};
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EVENT_TYPES = [
  { key: "check_in", label: "Check-in", color: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" },
  { key: "therapy", label: "Therapy", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400" },
  { key: "reminder", label: "Reminder", color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" },
  { key: "custom", label: "Custom", color: "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400" },
];

export default function CalendarPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calData, setCalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(searchParams.get("date") || null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ title: "", event_type: "check_in", date: "", time: "", notes: "" });
  const [exporting, setExporting] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/calendar/month?year=${year}&month=${month}`, { headers });
      setCalData(data);
    } catch { toast.error("Failed to load calendar"); }
    finally { setLoading(false); }
  }, [year, month, token]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/suggestions`, { headers });
      setSuggestions(data.suggestions || []);
    } catch { /* suggestions are optional */ }
  }, [token]);

  useEffect(() => { fetchMonth(); fetchSuggestions(); }, [fetchMonth, fetchSuggestions]);

  useEffect(() => {
    if (!selectedDate || !token) { setDayDetail(null); return; }
    setDayLoading(true);
    axios.get(`${API}/calendar/day/${selectedDate}`, { headers })
      .then(r => setDayDetail(r.data))
      .catch(() => toast.error("Failed to load day details"))
      .finally(() => setDayLoading(false));
  }, [selectedDate, token]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); setSelectedDate(now.toISOString().slice(0, 10)); };

  const grid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const getDayData = (day) => {
    if (!day || !calData) return null;
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return calData.days?.[key] || null;
  };
  const dateStr = (day) => `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  const scheduleEvent = async () => {
    if (!scheduleForm.title.trim()) { toast.error("Title required"); return; }
    if (!scheduleForm.date) { toast.error("Date required"); return; }
    try {
      await axios.post(`${API}/calendar/schedule`, scheduleForm, { headers });
      toast.success("Event scheduled!");
      setShowSchedule(false);
      setScheduleForm({ title: "", event_type: "check_in", date: "", time: "", notes: "" });
      fetchMonth();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to schedule"); }
  };

  const toggleEvent = async (eventId) => {
    try {
      await axios.put(`${API}/calendar/schedule/${eventId}/toggle`, {}, { headers });
      fetchMonth();
      if (selectedDate) {
        const { data } = await axios.get(`${API}/calendar/day/${selectedDate}`, { headers });
        setDayDetail(data);
      }
    } catch { toast.error("Failed to update event"); }
  };

  const deleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/calendar/schedule/${eventId}`, { headers });
      toast.success("Event deleted");
      fetchMonth();
      if (selectedDate) {
        const { data } = await axios.get(`${API}/calendar/day/${selectedDate}`, { headers });
        setDayDetail(data);
      }
    } catch { toast.error("Failed to delete event"); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/calendar/export/${year}/${month}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `omni_report_${MONTHS[month - 1].toLowerCase()}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!", { description: `${MONTHS[month - 1]} ${year} activity report` });
    } catch (err) {
      toast.error("Failed to export report");
    } finally { setExporting(false); }
  };

  const SUGGESTION_ICONS = {
    "calendar-check": CalendarIcon, flame: Flame, heart: Heart, clock: Clock, target: Target,
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2.5">
                <CalendarIcon className="w-6 h-6 text-primary" /> Calendar
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Your activity timeline, sessions & smart scheduling
              </p>
            </div>
            <div className="flex items-center gap-2">
              {calData?.streak > 0 && (
                <div data-testid="streak-badge" className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 dark:bg-orange-900/30 border border-orange-200/50 dark:border-orange-800/30">
                  <Flame className="w-4 h-4 text-orange-500" strokeWidth={2.5} />
                  <span className="text-sm font-bold text-orange-600 dark:text-orange-400">{calData.streak} day streak</span>
                </div>
              )}
              <Button data-testid="export-pdf-btn" onClick={exportPdf} disabled={exporting} variant="outline" className="gap-1.5 text-sm">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? "Exporting..." : "Export PDF"}
              </Button>
              <Button data-testid="schedule-btn" onClick={() => { setShowSchedule(true); setScheduleForm(f => ({ ...f, date: selectedDate || now.toISOString().slice(0, 10) })); }} variant="outline" className="gap-1.5 text-sm">
                <Plus className="w-4 h-4" /> Schedule
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar Grid */}
            <div className="lg:col-span-2 bg-card border border-border/50 rounded-2xl overflow-hidden">
              {/* Month Nav */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
                <button data-testid="cal-page-prev" onClick={prevMonth} className="p-2 rounded-xl hover:bg-muted/50 transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-heading font-semibold">{MONTHS[month - 1]} {year}</h2>
                  {!isCurrentMonth && (
                    <button data-testid="cal-today" onClick={goToday} className="text-xs text-primary hover:underline">Today</button>
                  )}
                </div>
                <button data-testid="cal-page-next" onClick={nextMonth} className="p-2 rounded-xl hover:bg-muted/50 transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Grid */}
              <div className="p-3 sm:p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                ) : (
                  <>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {DAYS.map(d => (
                        <div key={d} className="text-center text-xs font-semibold text-muted-foreground/50 py-1">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {grid.map((day, i) => {
                        const data = getDayData(day);
                        const isToday = isCurrentMonth && day === today;
                        const isSelected = day && selectedDate === dateStr(day);
                        const hasMood = data?.mood?.length > 0;
                        const hasChat = data?.chats?.length > 0;
                        const hasEvent = data?.events?.length > 0;
                        const topMood = hasMood ? data.mood[0].label : null;
                        const chatCount = data?.chats?.length || 0;

                        return (
                          <button
                            key={i}
                            data-testid={day ? `cal-page-day-${day}` : undefined}
                            onClick={() => day && setSelectedDate(dateStr(day))}
                            disabled={!day}
                            className={cn(
                              "relative flex flex-col items-center justify-center p-1 sm:p-2 rounded-xl transition-all min-h-[48px] sm:min-h-[64px]",
                              day ? "hover:bg-muted/60 cursor-pointer" : "invisible",
                              isToday && "ring-2 ring-primary font-bold",
                              isSelected && "bg-primary/10 ring-2 ring-primary",
                            )}
                          >
                            <span className={cn("text-sm", isToday && "text-primary")}>{day}</span>
                            {day && (hasMood || hasChat || hasEvent) && (
                              <div className="flex items-center gap-[3px] mt-1">
                                {hasMood && <span className={cn("w-[6px] h-[6px] rounded-full", MOOD_COLORS[topMood] || "bg-gray-400")} />}
                                {hasChat && (
                                  <span className="relative">
                                    <span className="w-[6px] h-[6px] rounded-full bg-blue-400 block" />
                                    {chatCount > 1 && (
                                      <span className="absolute -top-1 -right-2 text-[7px] font-bold text-blue-500">{chatCount}</span>
                                    )}
                                  </span>
                                )}
                                {hasEvent && <span className="w-[6px] h-[6px] rounded-full bg-violet-400" />}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Legend + Stats */}
                    <div className="flex items-center justify-between mt-3 px-1">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Mood</span>
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Chat</span>
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60"><span className="w-2.5 h-2.5 rounded-full bg-violet-400" /> Event</span>
                      </div>
                      {calData && (
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/50">
                          <span>{calData.total_moods} moods</span>
                          <span>{calData.total_chats} chats</span>
                          <span>{calData.total_events} events</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Panel */}
            <div className="space-y-4">
              {/* Day Detail */}
              {selectedDate && (
                <div data-testid="day-detail" className="bg-card border border-border/50 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">
                      {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                    </h3>
                    <button onClick={() => setSelectedDate(null)} className="p-1 rounded-lg hover:bg-muted/50">
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>

                  {dayLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                  ) : dayDetail ? (
                    <div className="space-y-3">
                      {/* Mood */}
                      {dayDetail.avg_mood !== null && (
                        <div className="flex items-center gap-2">
                          <Heart className={cn("w-4 h-4", dayDetail.avg_mood >= 7 ? "text-emerald-500" : dayDetail.avg_mood >= 4 ? "text-amber-500" : "text-red-500")} />
                          <span className="text-xs">Avg mood: <strong>{dayDetail.avg_mood}/10</strong></span>
                          {dayDetail.moods.length > 0 && (
                            <span className="text-[10px] text-muted-foreground/50">({dayDetail.moods.length} logs)</span>
                          )}
                        </div>
                      )}

                      {/* Conversations */}
                      {dayDetail.conversations?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-1.5">Conversations</p>
                          <div className="space-y-1.5">
                            {dayDetail.conversations.map(c => (
                              <button
                                key={c.id}
                                data-testid={`day-conv-${c.id}`}
                                onClick={() => navigate(`/chat/${c.id}`)}
                                className="w-full text-left p-2 rounded-xl hover:bg-muted/50 transition-colors"
                              >
                                <p className="text-xs font-medium truncate">{c.title}</p>
                                {c.preview && <p className="text-[10px] text-muted-foreground/60 line-clamp-2 mt-0.5">{c.preview}</p>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Scheduled Events */}
                      {dayDetail.events?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider mb-1.5">Events</p>
                          <div className="space-y-1.5">
                            {dayDetail.events.map(evt => {
                              const typeInfo = EVENT_TYPES.find(t => t.key === evt.event_type) || EVENT_TYPES[3];
                              return (
                                <div key={evt.id} className="flex items-center gap-2 p-2 rounded-xl bg-muted/20">
                                  <button
                                    data-testid={`evt-toggle-${evt.id}`}
                                    onClick={() => toggleEvent(evt.id)}
                                    className={cn("w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0",
                                      evt.completed ? "bg-primary border-primary" : "border-border hover:border-primary/50")}
                                  >
                                    {evt.completed && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <p className={cn("text-xs font-medium", evt.completed && "line-through text-muted-foreground")}>{evt.title}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <span className={cn("px-1.5 py-0.5 rounded text-[8px] font-semibold", typeInfo.color)}>{typeInfo.label}</span>
                                      {evt.time && <span className="text-[9px] text-muted-foreground/50">{evt.time}</span>}
                                    </div>
                                  </div>
                                  <button
                                    data-testid={`evt-delete-${evt.id}`}
                                    onClick={() => deleteEvent(evt.id)}
                                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground/40 hover:text-destructive transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Empty day */}
                      {!dayDetail.moods?.length && !dayDetail.conversations?.length && !dayDetail.events?.length && (
                        <div className="text-center py-4 text-muted-foreground/40">
                          <CalendarIcon className="w-8 h-8 mx-auto mb-1.5 opacity-30" />
                          <p className="text-xs">No activity this day</p>
                          <button
                            onClick={() => { setShowSchedule(true); setScheduleForm(f => ({ ...f, date: selectedDate })); }}
                            className="text-[11px] text-primary mt-1 hover:underline"
                          >
                            + Schedule something
                          </button>
                        </div>
                      )}

                      {dayDetail.total_messages > 0 && (
                        <p className="text-[10px] text-muted-foreground/40 text-center mt-2">
                          {dayDetail.total_messages} messages exchanged
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div data-testid="suggestions-panel" className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-primary" /> Smart Suggestions
                  </h3>
                  <div className="space-y-2">
                    {suggestions.map((s, i) => {
                      const SIcon = SUGGESTION_ICONS[s.icon] || Sparkles;
                      return (
                        <button
                          key={i}
                          data-testid={`suggestion-${i}`}
                          onClick={() => {
                            if (s.action === "mood_checkin") navigate("/mood");
                            else if (s.action === "view_goals") navigate("/goals");
                          }}
                          className="w-full flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-muted/50 transition-all text-left"
                        >
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <SIcon className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                          </div>
                          <div>
                            <p className="text-xs font-medium">{s.title}</p>
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">{s.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Goals Summary */}
              {calData?.goals_summary?.length > 0 && (
                <div className="bg-card border border-border/50 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
                    <Target className="w-4 h-4 text-primary" /> Active Goals
                  </h3>
                  <div className="space-y-2">
                    {calData.goals_summary.filter(g => g.status === "active").slice(0, 4).map(g => (
                      <div key={g.id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{g.title}</p>
                          <div className="w-full bg-muted/50 rounded-full h-1.5 mt-1">
                            <div className="bg-primary rounded-full h-1.5 transition-all" style={{ width: `${g.progress}%` }} />
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-muted-foreground">{g.progress}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowSchedule(false)}>
          <div className="bg-background rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" /> Schedule Event
              </h3>
              <button onClick={() => setShowSchedule(false)} className="p-1 rounded-lg hover:bg-muted/50"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                <input
                  data-testid="schedule-title"
                  type="text"
                  value={scheduleForm.title}
                  onChange={e => setScheduleForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Morning check-in..."
                  className="w-full px-3 py-2 rounded-xl border border-border bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
                <div className="flex items-center gap-1.5">
                  {EVENT_TYPES.map(t => (
                    <button
                      key={t.key}
                      data-testid={`schedule-type-${t.key}`}
                      onClick={() => setScheduleForm(f => ({ ...f, event_type: t.key }))}
                      className={cn(
                        "px-2.5 py-1.5 rounded-xl text-xs font-medium border transition-all",
                        scheduleForm.event_type === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted/50"
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label>
                  <input
                    data-testid="schedule-date"
                    type="date"
                    value={scheduleForm.date}
                    onChange={e => setScheduleForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Time (optional)</label>
                  <input
                    data-testid="schedule-time"
                    type="time"
                    value={scheduleForm.time}
                    onChange={e => setScheduleForm(f => ({ ...f, time: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-border bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes (optional)</label>
                <textarea
                  data-testid="schedule-notes"
                  value={scheduleForm.notes}
                  onChange={e => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Any details..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-border bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              <Button data-testid="schedule-submit" onClick={scheduleEvent} className="w-full gap-1.5">
                <CalendarIcon className="w-4 h-4" /> Schedule
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
